import { useApiSegmentsRange } from '@app/core/hooks/api/useApiSegmentsRange';
import { usePlaybackAutoPlay } from '@app/core/playback/hooks/usePlaybackAutoPlay';
import { Station } from '@app/core/types/stations';
import { PlaybackIconBtnV2, PlaybackIconBtnV2PlayingAction } from '@mi-front/ui';
import { Alert, Box, Skeleton } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';
import { RangeWaveformPlayerStream } from './RangeWaveformPlayerStream';
import { useRangeWaveformPlayback } from './useRangeWaveformPlayback';
import { useRangeWaveformPreload } from './useRangeWaveformPreload';

const noSegmentsMessage = defineMessage({
  id: 'playback.rangeWaveformPlayer.noSegments',
  defaultMessage: 'No hay audio disponible para este rango.',
});

export interface RangeWaveformPlayerContentProps {
  station: Station;
  from: number;
  to: number;
  height: number;
  /** Affordance while playing: pause (default) or stop (reset to range start). */
  playingAction?: PlaybackIconBtnV2PlayingAction;
  onPlayStarted?: () => void;
  /** When true, start playback once segments are available. */
  autoPlay?: boolean;
}

/**
 * Inner UI for the range waveform player: play / pause-or-stop button and stream.
 * Must render inside a SegmentsAudioPlaybackProvider (or parent PlaybackProvider).
 */
export function RangeWaveformPlayerContent({
  station,
  from,
  to,
  height,
  playingAction = 'pause',
  onPlayStarted,
  autoPlay = false,
}: RangeWaveformPlayerContentProps) {
  const { data: segments, isLoading, isError } = useApiSegmentsRange(station.id, from, to);
  const { showPause, isSeeking, currentTs, togglePlayback, seek } = useRangeWaveformPlayback({
    from,
    to,
    playingAction,
    onPlayStarted,
  });
  useRangeWaveformPreload(from);
  usePlaybackAutoPlay(autoPlay && !isLoading && !!segments?.length);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        <Skeleton variant="circular" width={48} height={48} sx={{ flexShrink: 0 }} />
        <Skeleton
          variant="rounded"
          sx={{
            flex: 1,
            height,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            boxSizing: 'border-box',
          }}
        />
      </Box>
    );
  }

  if (isError || !segments?.length) {
    return (
      <Alert severity="info" sx={{ width: '100%' }}>
        <FormattedMessage {...noSegmentsMessage} />
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
      <PlaybackIconBtnV2
        size="large"
        isPlaying={showPause}
        isLoading={isSeeking}
        playingAction={playingAction}
        onClick={togglePlayback}
      />
      <RangeWaveformPlayerStream
        from={from}
        to={to}
        segments={segments}
        height={height}
        currentTs={currentTs}
        onSeek={seek}
        tz={station.timezone}
      />
    </Box>
  );
}
